import { Tabs as MUITabs, Tab, styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import Box from 'shell/components/Box';
import TabPanel from 'shell/components/TabPanel';

const CustomTabs = styled(MUITabs)(() => {
    return {
        margin: '-3px',
        minHeight: 'unset',
        '& .MuiTabs-flexContainer': {
            height: 'calc(2.125rem + 6px)',
            padding: '3px',
        },
        '& .MuiTabs-indicator': {
            display: 'none',
        },
    };
});

const CustomTab = styled(Tab)(({ device, $disabled, theme }) => {
    const size = {
        mobile: 'small',
        tablet: 'large',
        desktop: 'large',
    }[device];

    return {
        flex: 1,
        maxWidth: 'unset',
        minHeight: 'unset',
        height: '2.125rem',
        padding: theme.spacing(2, 1),
        background: theme.palette.ensightful.neutral[100],
        fontSize: `${theme.typography.p2.fontSize[size]}rem`,
        fontWeight: theme.typography.p2.fontWeight.medium,
        letterSpacing: `${theme.typography.p2.letterSpacing[size]}rem`,
        lineHeight: `${theme.typography.getLineHeight('p2', size)}rem`,
        color: theme.palette.ensightful.neutral[700],
        transition: theme.transitions.default,
        '&:first-child': {
            borderTopLeftRadius: '4px',
            borderBottomLeftRadius: '4px',
        },
        '&:last-child': {
            borderTopRightRadius: '4px',
            borderBottomRightRadius: '4px',
        },
        '&:hover': {
            background: theme.palette.ensightful.neutral[300],
        },
        '&:active': {
            background: theme.palette.ensightful.neutral[400],
        },
        '&&.Mui-focusVisible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-5px',
        },
        '&.Mui-selected': {
            background: theme.palette.ensightful.primary[50],
            color: theme.palette.ensightful.primary[700],
        },
        ...($disabled && {
            background: theme.palette.ensightful.neutral[300],
            color: theme.palette.ensightful.neutral[500],
            pointerEvents: 'none',
        }),
        ...(device === 'mobile' && {
            minWidth: 0,
        }),
    };
});

const Tabs = ({ options, selected, onChange, ...rest }) => {
    if (!rest['aria-label'] && !rest['aria-labelledby']) {
        throw new Error('Shell: One of aria-label or aria-labelledby is required in component Tabs.');
    }

    const { device } = useViewport();

    return (
        <Box {...omit(rest, ['aria-label'])}>
            <CustomTabs
                aria-label={rest['aria-label']}
                aria-labelledby={rest['aria-labelledby']}
                value={options.findIndex(({ key }) => key === selected)}
                onChange={(_, index) => onChange(options[index].key)}
            >
                {options.map(({ key, value, disabled }) => (
                    <CustomTab
                        id={`tab-${key}`}
                        key={key}
                        aria-controls={`tabpanel-${key}`}
                        aria-disabled={disabled}
                        device={device}
                        $disabled={disabled}
                        label={value}
                        onKeyDown={(e) => {
                            if (disabled) e.preventDefault();
                        }}
                    />
                ))}
            </CustomTabs>
            {options.map(({ key, content }) => (
                <TabPanel key={key} direction={'column'} $key={key} selected={selected}>
                    {content}
                </TabPanel>
            ))}
        </Box>
    );
};

export default Tabs;
